<template>
    <div v-if="legs" :class="['journeys', `direction-${direction}`]" :data-type="dataType">
        <div
            v-for="(journey, index) in journeys"
            :key="index"
            :class="['journeys-leg', { single: legs.length === 1 }]">
            <component
                :is="type"
                :data="journey"
                :dataType="dataType"
                :isOutbound="index === 0"
                :showDetailed="showDetailed"
                :showHeader="showHeader"
                :showHeaderBackground="showHeaderBackground"
                :showAddons="showAddons"
                :inlineHeader="inlineHeader"
                :showDivider="showDivider" />
            <Divider
                v-if="isColumnLayout && index < journeys.length - 1"
                color="lightGrey"
                bottomMargin="xxl"
                topMargin="xxl"
                thickness="1" />
        </div>
    </div>
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { FlightLeg, FerryLeg } from '@/api/Cart/cart.interfaces';
    import {
        FlightLeg as FlightLegMyBooking,
        FerryLeg as FerryLegMyBooking,
    } from '@/api/MyBooking/myBooking.interfaces';
    import { MapDataType, mapFerryJourney, mapFlightJourney } from '@shared/helpers/journey';
    import { Journey as JourneyModel } from '@ui/components/Journey/Journey.interfaces';
    import { PropType, computed, defineComponent } from 'vue';
    import { useScreenSize } from '@shared/helpers/viewport';
    import Flight from './Flight/Journey.vue';
    import Ferry from './Ferry/Journey.vue';
    import Divider from '@ui/components/Divider/Divider.vue';

    export default defineComponent({
        name: 'Journeys',
        methods: { t },
        components: { Divider, Flight, Ferry },
        props: {
            type: String as PropType<'Flight' | 'Ferry'>,
            legs: Array as PropType<
                FlightLeg[] | FlightLegMyBooking[] | FerryLeg[] | FerryLegMyBooking[]
            >,
            showDetailed: { type: Boolean, default: false },
            showAddons: { type: Boolean, default: true },
            showHeader: { type: Boolean, default: true },
            showHeaderBackground: { type: Boolean, default: true },
            inlineHeader: { type: Boolean, default: false },
            direction: {
                type: String as PropType<'row' | 'column'>,
                default: 'row',
            },
            dataType: {
                type: String as PropType<MapDataType>,
                default: MapDataType.Checkout,
            },
            showDivider: { type: Boolean, default: false },
        },
        setup(props) {
            const { isLargeDesktop } = useScreenSize();

            const journeys = computed<JourneyModel[]>(() => {
                switch (props.type) {
                    case 'Flight': {
                        const legs = props.legs as FlightLeg[] | FlightLegMyBooking[];

                        return (legs || []).map((l) => mapFlightJourney(l, props.dataType));
                    }
                    case 'Ferry': {
                        const legs = props.legs as FerryLeg[] | FerryLegMyBooking[];

                        return (legs || []).map((l) => mapFerryJourney(l, props.dataType));
                    }
                    default:
                        return [];
                }
            });

            const isColumnLayout = computed(
                () => props.direction === 'column' || !isLargeDesktop.value
            );

            return { journeys, isColumnLayout };
        },
    });
</script>

<style lang="scss" scoped>
    @forward './Journeys.scss';
</style>
